/**
 * Feedback analytics panel.
 *
 * Admin QA dashboard for item ratings, question hotspots, and traceable
 * feedback events.
 *
 * @module FeedbackAnalytics
 */
import { Fragment, useMemo, useState } from 'react';
import type { ComponentProps } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  HelpCircle,
  Loader2,
  MessageSquareHeart,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import QuestionPreview from '@/components/admin/QuestionPreview';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useFeedbackAnalytics } from '@/hooks/useFeedbackAnalytics';
import { getQuestionById } from '@/lib/questionApi';
import {
  AI_FEEDBACK_DISLIKE_REASONS_READING_LISTENING,
  AI_FEEDBACK_DISLIKE_REASONS_WRITING_SPEAKING,
  QUESTION_DISLIKE_REASONS,
  type ReasonOption,
} from '@/lib/ratingConstants';
import type {
  FeedbackEventTrace,
  ModuleRatingVolume,
  QuestionHotspotItem,
  ReasonVolume,
} from '@/lib/feedbackAnalyticsApi';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

type HotspotView = 'mostDisliked' | 'mostLiked' | 'highestDislikeRate';
type MetricIcon = LucideIcon;
type PreviewQuestion = ComponentProps<typeof QuestionPreview>['question'];

const MODULE_OPTIONS = ['reading', 'listening', 'writing', 'speaking'] as const;
const TARGET_TYPE_OPTIONS = ['question', 'ai_feedback'] as const;
const RATING_OPTIONS = ['like', 'dislike'] as const;
const PAGE_LIMIT = 10;

function pct(rate?: number | null): string {
  return rate == null ? '-' : `${Math.round(rate * 100)}%`;
}

function numberValue(value?: number | null): string {
  return value == null ? '-' : value.toLocaleString();
}

function decimalValue(value?: number | null): string {
  return value == null ? '-' : String(Math.round(value * 10) / 10);
}

function progressValue(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function preview(value?: string | null, max = 110): string {
  const text = (value ?? '').trim();
  if (!text) return '-';
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function uniqueReasonOptions(targetType: string): ReasonOption[] {
  const base =
    targetType === 'question'
      ? QUESTION_DISLIKE_REASONS
      : targetType === 'ai_feedback'
        ? [
            ...AI_FEEDBACK_DISLIKE_REASONS_WRITING_SPEAKING,
            ...AI_FEEDBACK_DISLIKE_REASONS_READING_LISTENING,
          ]
        : [
            ...QUESTION_DISLIKE_REASONS,
            ...AI_FEEDBACK_DISLIKE_REASONS_WRITING_SPEAKING,
            ...AI_FEEDBACK_DISLIKE_REASONS_READING_LISTENING,
          ];

  const seen = new Set<string>();
  return base.filter((option) => {
    if (seen.has(option.code)) return false;
    seen.add(option.code);
    return true;
  });
}

function EmptyState({ label }: { readonly label: string }) {
  return (
    <div className="font-ui rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function MetricCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  readonly title: string;
  readonly value: string | number;
  readonly description: string;
  readonly icon: MetricIcon;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-ui text-sm text-muted-foreground">{title}</p>
            <p className="font-ui mt-1 text-3xl font-bold text-primary">{value}</p>
            <p className="font-ui mt-1 text-xs text-muted-foreground">{description}</p>
          </div>
          <Icon className="h-8 w-8 flex-shrink-0 text-primary" aria-hidden />
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressRow({
  label,
  meta,
  value,
  footer,
}: {
  readonly label: string;
  readonly meta: string;
  readonly value: number;
  readonly footer?: string;
}) {
  return (
    <div className="space-y-2 rounded-lg bg-muted/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="font-ui text-sm font-medium text-foreground">{label}</span>
        <span className="font-ui text-sm text-muted-foreground">{meta}</span>
      </div>
      <Progress value={value} className="h-2" />
      {footer ? <p className="font-ui text-xs text-muted-foreground">{footer}</p> : null}
    </div>
  );
}

function TraceField({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value?: string | number | null;
  readonly mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'font-ui whitespace-pre-wrap break-words text-sm text-foreground',
          mono && 'font-mono text-xs',
        )}
      >
        {value === undefined || value === null || value === '' ? '-' : value}
      </p>
    </div>
  );
}

export default function FeedbackAnalytics() {
  const { t } = useTranslation();
  const [moduleFilter, setModuleFilter] = useState('all');
  const [targetTypeFilter, setTargetTypeFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [reasonCodeFilter, setReasonCodeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [page, setPage] = useState(1);
  const [hotspotView, setHotspotView] = useState<HotspotView>('mostDisliked');
  const [expandedRatingId, setExpandedRatingId] = useState<string | null>(null);
  const [previewQuestion, setPreviewQuestion] = useState<PreviewQuestion | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      module: moduleFilter,
      targetType: targetTypeFilter,
      rating: ratingFilter,
      reasonCode: reasonCodeFilter,
      dateFrom,
      dateTo,
      questionId: questionId.trim(),
      page,
      limit: PAGE_LIMIT,
    }),
    [dateFrom, dateTo, moduleFilter, page, questionId, ratingFilter, reasonCodeFilter, targetTypeFilter],
  );
  const { analytics, isLoading, isError } = useFeedbackAnalytics(filters);

  const reasonOptions = useMemo(
    () => uniqueReasonOptions(targetTypeFilter),
    [targetTypeFilter],
  );

  const filtersActive = Boolean(
    moduleFilter !== 'all' ||
      targetTypeFilter !== 'all' ||
      ratingFilter !== 'all' ||
      reasonCodeFilter !== 'all' ||
      dateFrom ||
      dateTo ||
      questionId.trim(),
  );

  const setFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
    setExpandedRatingId(null);
  };

  const resetFilters = () => {
    setModuleFilter('all');
    setTargetTypeFilter('all');
    setRatingFilter('all');
    setReasonCodeFilter('all');
    setDateFrom('');
    setDateTo('');
    setQuestionId('');
    setPage(1);
    setExpandedRatingId(null);
  };

  const handlePreview = async (row: FeedbackEventTrace) => {
    if (!row.questionId) return;
    try {
      setPreviewLoadingId(row.ratingId);
      const question = await getQuestionById(row.questionId);
      setPreviewQuestion(question);
    } catch (error) {
      logger.error('Failed to load question preview from feedback analytics', error);
      toast.error(t('feedbackAnalytics.events.previewError'));
    } finally {
      setPreviewLoadingId(null);
    }
  };

  if (isLoading && !analytics) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary/40" aria-hidden />
      </div>
    );
  }

  if (isError) {
    return <p className="font-ui py-12 text-center text-sm text-muted-foreground">{t('feedbackAnalytics.error')}</p>;
  }

  if (!analytics) {
    return <p className="font-ui py-12 text-center text-sm text-muted-foreground">{t('feedbackAnalytics.empty')}</p>;
  }

  const { summary, likeDislikeByModule, dislikeByReason, wrongLevel, reviewOperations, questionHotspots, feedbackEvents } = analytics;
  const safeLikeDislikeByModule = likeDislikeByModule ?? [];
  const safeDislikeByReason = dislikeByReason ?? [];
  const hotspotRows = questionHotspots?.[hotspotView] ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title={t('feedbackAnalytics.summary.total')}
          value={numberValue(summary?.totalRatings)}
          description={t('feedbackAnalytics.summary.totalHelp')}
          icon={MessageSquareHeart}
        />
        <MetricCard
          title={t('feedbackAnalytics.summary.likes')}
          value={numberValue(summary?.likeCount)}
          description={t('feedbackAnalytics.summary.likesHelp', { rate: pct(summary?.dislikeRate) })}
          icon={ThumbsUp}
        />
        <MetricCard
          title={t('feedbackAnalytics.summary.dislikes')}
          value={numberValue(summary?.dislikeCount)}
          description={t('feedbackAnalytics.summary.dislikesHelp', { rate: pct(summary?.dislikeRate) })}
          icon={ThumbsDown}
        />
        <MetricCard
          title={t('feedbackAnalytics.summary.questions')}
          value={numberValue(summary?.affectedQuestions)}
          description={t('feedbackAnalytics.summary.questionsHelp')}
          icon={HelpCircle}
        />
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="mr-auto flex min-w-[180px] items-center gap-2 pb-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" aria-hidden />
              <span className="font-ui">{t('feedbackAnalytics.filters.title')}</span>
            </div>

            <FilterSelect
              id="feedback-module-filter"
              label={t('feedbackAnalytics.filters.module')}
              value={moduleFilter}
              onValueChange={setFilter(setModuleFilter)}
              options={[
                { value: 'all', label: t('feedbackAnalytics.filters.allModules') },
                ...MODULE_OPTIONS.map((module) => ({
                  value: module,
                  label: moduleLabel(module),
                })),
              ]}
            />

            <FilterSelect
              id="feedback-target-filter"
              label={t('feedbackAnalytics.filters.targetType')}
              value={targetTypeFilter}
              onValueChange={(value) => {
                setFilter(setTargetTypeFilter)(value);
                setReasonCodeFilter('all');
              }}
              options={[
                { value: 'all', label: t('feedbackAnalytics.filters.allTargets') },
                ...TARGET_TYPE_OPTIONS.map((targetType) => ({
                  value: targetType,
                  label: targetTypeLabel(targetType),
                })),
              ]}
            />

            <FilterSelect
              id="feedback-rating-filter"
              label={t('feedbackAnalytics.filters.rating')}
              value={ratingFilter}
              onValueChange={setFilter(setRatingFilter)}
              options={[
                { value: 'all', label: t('feedbackAnalytics.filters.allRatings') },
                ...RATING_OPTIONS.map((rating) => ({
                  value: rating,
                  label: ratingLabel(rating),
                })),
              ]}
            />

            <FilterSelect
              id="feedback-reason-filter"
              label={t('feedbackAnalytics.filters.reason')}
              value={reasonCodeFilter}
              onValueChange={setFilter(setReasonCodeFilter)}
              options={[
                { value: 'all', label: t('feedbackAnalytics.filters.allReasons') },
                ...(reasonOptions ?? []).map((option) => ({
                  value: option.code,
                  label: reasonLabel(option.code, option.labelKey),
                })),
              ]}
            />

            <div className="space-y-1.5">
              <Label htmlFor="feedback-date-from" className="font-ui text-xs">
                {t('feedbackAnalytics.filters.dateFrom')}
              </Label>
              <Input
                id="feedback-date-from"
                className="font-ui w-[145px] bg-background"
                type="date"
                value={dateFrom}
                onChange={(event) => setFilter(setDateFrom)(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-date-to" className="font-ui text-xs">
                {t('feedbackAnalytics.filters.dateTo')}
              </Label>
              <Input
                id="feedback-date-to"
                className="font-ui w-[145px] bg-background"
                type="date"
                value={dateTo}
                onChange={(event) => setFilter(setDateTo)(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-question-id" className="font-ui text-xs">
                {t('feedbackAnalytics.filters.questionId')}
              </Label>
              <Input
                id="feedback-question-id"
                className="font-ui w-[220px] bg-background"
                value={questionId}
                placeholder={t('feedbackAnalytics.filters.questionIdPlaceholder')}
                onChange={(event) => setFilter(setQuestionId)(event.target.value)}
              />
            </div>

            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={resetFilters}
              disabled={!filtersActive}
              aria-label={t('feedbackAnalytics.filters.reset')}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="font-ui flex items-center gap-2">
              <BarChart3 className="h-5 w-5" aria-hidden />
              {t('feedbackAnalytics.volumes.title')}
            </CardTitle>
            <CardDescription className="font-ui">
              {t('feedbackAnalytics.volumes.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeLikeDislikeByModule.length === 0 ? (
              <EmptyState label={t('feedbackAnalytics.empty')} />
            ) : (
              safeLikeDislikeByModule.map((row) => (
                <ModuleSentimentRow key={row.module} row={row} />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-ui flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" aria-hidden />
              {t('feedbackAnalytics.reasons.title')}
            </CardTitle>
            <CardDescription className="font-ui">
              {t('feedbackAnalytics.reasons.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeDislikeByReason.length === 0 ? (
              <EmptyState label={t('feedbackAnalytics.empty')} />
            ) : (
              safeDislikeByReason.slice(0, 8).map((row) => (
                <ReasonProgressRow
                  key={`${row.targetType}-${row.reasonCode}`}
                  row={row}
                  total={summary?.dislikeCount ?? 0}
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="font-ui flex items-center gap-2">
              <Activity className="h-5 w-5" aria-hidden />
              {t('feedbackAnalytics.hotspots.title')}
            </CardTitle>
            <CardDescription className="font-ui">
              {t('feedbackAnalytics.hotspots.description')}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {(['mostDisliked', 'mostLiked', 'highestDislikeRate'] as const).map((view) => (
              <Button
                key={view}
                type="button"
                variant={hotspotView === view ? 'default' : 'outline'}
                size="sm"
                className="font-ui"
                onClick={() => setHotspotView(view)}
              >
                {t(`feedbackAnalytics.hotspots.${view}`)}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {hotspotRows.length === 0 ? (
            <EmptyState label={t('feedbackAnalytics.empty')} />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {hotspotRows.map((row, index) => (
                <HotspotCard key={`${hotspotView}-${row.questionId}`} row={row} rank={index + 1} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <Card>
          <CardHeader>
            <CardTitle className="font-ui flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
              {t('feedbackAnalytics.wrongLevel.title')}
            </CardTitle>
            <CardDescription className="font-ui">
              {t('feedbackAnalytics.wrongLevel.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <SmallStat label={t('feedbackAnalytics.wrongLevel.total')} value={wrongLevel?.total ?? 0} />
            <SmallStat label={t('feedbackAnalytics.wrongLevel.rate')} value={pct(wrongLevel?.correctnessRate)} />
            <SmallStat label={t('feedbackAnalytics.wrongLevel.correct')} value={wrongLevel?.answeredCorrectly ?? 0} />
            <SmallStat label={t('feedbackAnalytics.wrongLevel.incorrect')} value={wrongLevel?.answeredIncorrectly ?? 0} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-ui flex items-center gap-2">
              <MessageSquareHeart className="h-5 w-5" aria-hidden />
              {t('feedbackAnalytics.operations.title')}
            </CardTitle>
            <CardDescription className="font-ui">
              {t('feedbackAnalytics.operations.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SmallStat label={t('feedbackAnalytics.operations.total')} value={reviewOperations?.totalRequests ?? 0} />
            <SmallStat label={t('feedbackAnalytics.operations.pending')} value={reviewOperations?.pendingBacklog ?? 0} />
            <SmallStat label={t('feedbackAnalytics.operations.completed')} value={reviewOperations?.completedCount ?? 0} />
            <SmallStat label={t('feedbackAnalytics.operations.avgTurnaround')} value={decimalValue(reviewOperations?.avgTurnaroundHours)} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-ui flex items-center gap-2">
            <MessageSquareHeart className="h-5 w-5" aria-hidden />
            {t('feedbackAnalytics.events.title')}
          </CardTitle>
          <CardDescription className="font-ui">
            {t('feedbackAnalytics.events.description', {
              total: (feedbackEvents?.total ?? 0).toLocaleString(),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FeedbackEventsTable
            rows={feedbackEvents?.items ?? []}
            expandedRatingId={expandedRatingId}
            previewLoadingId={previewLoadingId}
            onToggleExpand={(ratingId) => setExpandedRatingId(expandedRatingId === ratingId ? null : ratingId)}
            onPreview={handlePreview}
          />

          <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-end">
            <p className="font-ui text-sm text-muted-foreground">
              {t('feedbackAnalytics.events.pageStatus', {
                page: feedbackEvents?.page ?? 1,
                totalPages: feedbackEvents?.totalPages ?? 1,
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={(feedbackEvents?.page ?? 1) <= 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                <span className="font-ui">{t('feedbackAnalytics.events.previous')}</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((current) => Math.min(feedbackEvents?.totalPages ?? 1, current + 1))}
                disabled={(feedbackEvents?.page ?? 1) >= (feedbackEvents?.totalPages ?? 1) || isLoading}
              >
                <span className="font-ui">{t('feedbackAnalytics.events.next')}</span>
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!previewQuestion}
        onOpenChange={(open) => !open && setPreviewQuestion(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-ui">{t('feedbackAnalytics.events.previewTitle')}</DialogTitle>
            <DialogDescription className="font-ui">
              {t('feedbackAnalytics.events.previewDescription')}
            </DialogDescription>
          </DialogHeader>
          {previewQuestion ? (
            <QuestionPreview
              question={previewQuestion}
              onClose={() => setPreviewQuestion(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );

  function FilterSelect({
    id,
    label,
    value,
    onValueChange,
    options,
  }: {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    readonly onValueChange: (value: string) => void;
    readonly options: readonly { readonly value: string; readonly label: string }[];
  }) {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="font-ui text-xs">
          {label}
        </Label>
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger id={id} className="font-ui w-[150px] bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span className="font-ui">{option.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  function SmallStat({ label, value }: { readonly label: string; readonly value: string | number }) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-ui mt-1 text-xl font-bold text-foreground">{value}</p>
      </div>
    );
  }

  function ModuleSentimentRow({ row }: { readonly row: ModuleRatingVolume }) {
    const total = row.likeCount + row.dislikeCount;
    const dislikePercent = progressValue(row.dislikeCount, total);
    return (
      <ProgressRow
        label={moduleLabel(row.module)}
        meta={t('feedbackAnalytics.volumes.sentimentMeta', {
          likes: row.likeCount,
          dislikes: row.dislikeCount,
        })}
        value={dislikePercent}
        footer={t('feedbackAnalytics.volumes.sentimentFooter', {
          rate: `${dislikePercent}%`,
          total,
        })}
      />
    );
  }

  function ReasonProgressRow({ row, total }: { readonly row: ReasonVolume; readonly total: number }) {
    const value = progressValue(row.count, total);
    return (
      <ProgressRow
        label={reasonLabel(row.reasonCode)}
        meta={`${row.count}`}
        value={value}
        footer={`${targetTypeLabel(row.targetType)} · ${value}%`}
      />
    );
  }

  function HotspotCard({ row, rank }: { readonly row: QuestionHotspotItem; readonly rank: number }) {
    const total = row.totalRatings || row.likeCount + row.dislikeCount;
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-ui text-xs font-bold text-primary">#{rank}</span>
              <Badge variant="outline" className="font-ui text-xs">
                {moduleLabel(row.module)}
              </Badge>
              {row.level ? (
                <Badge variant="secondary" className="font-ui text-xs">
                  {row.level}
                </Badge>
              ) : null}
              {row.section ? (
                <Badge variant="outline" className="font-ui text-xs">
                  {t('feedbackAnalytics.trace.sectionShort', { section: row.section })}
                </Badge>
              ) : null}
            </div>
            <p className="font-ui text-sm font-medium text-foreground">{preview(row.questionText, 150)}</p>
            <p className="font-mono text-xs text-muted-foreground">{row.questionId}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="font-ui text-sm font-bold text-foreground">{pct(row.dislikeRate)}</p>
            <p className="font-ui text-xs text-muted-foreground">{t('feedbackAnalytics.hotspots.rate')}</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <SmallInlineStat label={t('feedbackAnalytics.hotspots.likes')} value={row.likeCount} />
          <SmallInlineStat label={t('feedbackAnalytics.hotspots.dislikes')} value={row.dislikeCount} />
          <SmallInlineStat label={t('feedbackAnalytics.hotspots.total')} value={total} />
        </div>
      </div>
    );
  }

  function SmallInlineStat({ label, value }: { readonly label: string; readonly value: string | number }) {
    return (
      <div className="rounded-md bg-background p-2">
        <p className="font-ui text-sm font-semibold text-foreground">{value}</p>
        <p className="font-ui text-[11px] text-muted-foreground">{label}</p>
      </div>
    );
  }

  function FeedbackEventsTable({
    rows,
    expandedRatingId,
    previewLoadingId,
    onToggleExpand,
    onPreview,
  }: {
    readonly rows: FeedbackEventTrace[];
    readonly expandedRatingId: string | null;
    readonly previewLoadingId: string | null;
    readonly onToggleExpand: (ratingId: string) => void;
    readonly onPreview: (row: FeedbackEventTrace) => void;
  }) {
    if (rows.length === 0) return <EmptyState label={t('feedbackAnalytics.empty')} />;

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="font-ui">{t('feedbackAnalytics.events.rating')}</TableHead>
              <TableHead className="font-ui">{t('feedbackAnalytics.events.question')}</TableHead>
              <TableHead className="font-ui">{t('feedbackAnalytics.events.signal')}</TableHead>
              <TableHead className="font-ui">{t('feedbackAnalytics.events.user')}</TableHead>
              <TableHead className="font-ui">{t('feedbackAnalytics.events.date')}</TableHead>
              <TableHead className="w-[70px] font-ui">{t('feedbackAnalytics.events.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const expanded = expandedRatingId === row.ratingId;
              return (
                <Fragment key={row.ratingId}>
                  <TableRow className="align-top">
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onToggleExpand(row.ratingId)}
                        aria-label={
                          expanded
                            ? t('feedbackAnalytics.events.collapse')
                            : t('feedbackAnalytics.events.expand')
                        }
                      >
                        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <RatingBadge rating={row.rating} />
                      <p className="font-ui mt-1 text-xs text-muted-foreground">{targetTypeLabel(row.targetType)}</p>
                    </TableCell>
                    <TableCell className="font-ui">
                      <div className="max-w-[420px] space-y-1">
                        <p className="font-ui text-sm font-medium text-foreground">{preview(row.questionText, 120)}</p>
                        <p className="font-ui text-xs text-muted-foreground">
                          {moduleLabel(row.module)}
                          {row.level ? ` · ${row.level}` : ''}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ReasonBadges codes={row.reasonCodes} />
                      <p className="font-ui mt-1 text-xs text-muted-foreground">{correctnessLabel(row)}</p>
                    </TableCell>
                    <TableCell className="font-ui text-sm">
                      <div className="max-w-[180px] truncate">
                        {row.username || row.email || row.userId || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="font-ui text-sm">{formatDateTime(row.updatedAt)}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onPreview(row)}
                        disabled={!row.questionId || previewLoadingId === row.ratingId}
                        aria-label={t('feedbackAnalytics.events.previewTitle')}
                      >
                        {previewLoadingId === row.ratingId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded ? (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/20 p-4">
                        <TraceDetails row={row} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  function RatingBadge({ rating }: { readonly rating: string }) {
    const liked = rating === 'like';
    return (
      <Badge variant={liked ? 'default' : 'destructive'} className="font-ui text-xs">
        {ratingLabel(rating)}
      </Badge>
    );
  }

  function ReasonBadges({ codes }: { readonly codes: string[] }) {
    if (!codes.length) {
      return <span className="font-ui text-sm text-muted-foreground">{t('feedbackAnalytics.events.noReason')}</span>;
    }
    return (
      <div className="flex max-w-[260px] flex-wrap gap-1">
        {codes.map((code) => (
          <Badge key={code} variant="outline" className="font-ui text-[11px]">
            {reasonLabel(code)}
          </Badge>
        ))}
      </div>
    );
  }

  function TraceDetails({ row }: { readonly row: FeedbackEventTrace }) {
    return (
      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-4">
          <TraceField label={t('feedbackAnalytics.trace.fullQuestion')} value={row.questionText} />
          <TraceField label={t('feedbackAnalytics.trace.answer')} value={row.answerText} />
          <TraceField label={t('feedbackAnalytics.trace.expectedAnswer')} value={row.expectedAnswer} />
          <TraceField label={t('feedbackAnalytics.trace.explanation')} value={row.explanation} />
          <TraceField label={t('feedbackAnalytics.trace.aiFeedback')} value={row.aiFeedbackSummary} />
          <TraceField label={t('feedbackAnalytics.trace.freeText')} value={row.freeText} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TraceField label={t('feedbackAnalytics.trace.ratingId')} value={row.ratingId} mono />
          <TraceField label={t('feedbackAnalytics.trace.targetId')} value={row.targetId} mono />
          <TraceField label={t('feedbackAnalytics.trace.questionId')} value={row.questionId} mono />
          <TraceField label={t('feedbackAnalytics.trace.sessionId')} value={row.sessionId} mono />
          <TraceField label={t('feedbackAnalytics.trace.userId')} value={row.userId} mono />
          <TraceField label={t('feedbackAnalytics.trace.email')} value={row.email} />
          <TraceField label={t('feedbackAnalytics.trace.level')} value={row.level} />
          <TraceField label={t('feedbackAnalytics.trace.section')} value={row.section} />
          <TraceField label={t('feedbackAnalytics.trace.examType')} value={row.examType.join(', ')} />
          <TraceField label={t('feedbackAnalytics.trace.createdAt')} value={formatDateTime(row.createdAt)} />
        </div>
      </div>
    );
  }

  function moduleLabel(module?: string | null) {
    if (!module) return '-';
    return t(`moduleSelection.modules.${module}.label`, { defaultValue: module });
  }

  function targetTypeLabel(targetType?: string | null) {
    if (targetType === 'ai_feedback') {
      return t('feedbackAnalytics.targetTypes.aiFeedback');
    }
    if (targetType === 'question') {
      return t('feedbackAnalytics.targetTypes.question');
    }
    return '-';
  }

  function ratingLabel(rating?: string | null) {
    if (rating === 'like') return t('feedbackAnalytics.ratings.like');
    if (rating === 'dislike') return t('feedbackAnalytics.ratings.dislike');
    return rating || '-';
  }

  function reasonLabel(code: string, labelKey?: string) {
    if (labelKey) {
      return t(labelKey, { defaultValue: code });
    }
    const option = uniqueReasonOptions('all').find((item) => item.code === code);
    return option ? t(option.labelKey, { defaultValue: code }) : code;
  }

  function correctnessLabel(row: FeedbackEventTrace) {
    if (row.isCorrect === true) return t('feedbackAnalytics.trace.correct');
    if (row.isCorrect === false) return t('feedbackAnalytics.trace.incorrect');
    return row.points == null ? '-' : `${row.points} ${t('feedbackAnalytics.trace.points')}`;
  }
}
